import { AgAuthenticationService } from '@ag/vc.ag-core/authentication';
import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { AgTranslateService } from '@ag/vc.ag-core/translate';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BeEvents } from 'src/app/shared/models/BackendEventLogExports';
import { OnboardingStatus } from 'src/app/shared/models/OnBoardingStatus';
import { UserLoginRequestModel, UserLoginResponseModel } from 'src/app/shared/models/UserLogin';
import { BackendEventLogService } from 'src/app/shared/services/backend-event-log.service';
import { APIFailure, ProcessCd, SurveyFlow } from '../../../core/models/context';
import { SessionService } from '../../../core/services/session.service';
import { ContactDetails, TOUResponseModel } from '../../../shared/models/ContactDetails';
import { OnlinePaymentEntryPage, PaymentDetails } from '../../../shared/models/Contract';
import { DeepLinkingModel } from '../../../shared/models/DeeplinkParams';
import { createUserResponse } from '../../../shared/models/IdentityModel';
import { ProductCodeRange } from '../../../shared/models/LeadProducts';
import { LogLevel, LogReqModel } from '../../../shared/models/log';
import { EligibilityReason, IsSubscriptionAllowedReqObj, IsSubscriptionAllowedResponse } from '../../../shared/models/PersonAndPreferences';
import { PersonDetails } from '../../../shared/models/PersonDetailsModel';
import { prospect, SilUnsubscribeType, UserPreferences } from '../../../shared/models/ProspectModel';
import { SepaDeeplinkModel } from '../../../shared/models/SepaDeeplinkModel';
import { SessionKeys } from '../../../shared/models/SessionKeys';
import { StartParameter } from '../../../shared/models/StartParameter';
import { StatusCode } from '../../../shared/models/StatusCode';
import { SimulationSessionData } from '../../../shared/models/SurveyModel';
import { RouteNames } from '../../../shared/routing/app-routing.constants';
import { FactoryhelperService } from '../../../shared/services/factoryHelper.service';
import { HelperService } from '../../../shared/services/helper.service';
import { CustomerService } from '../../customer-zone/services/customer-service.service';
import { RouteConstant } from '../../e-sign/e-sign-routing.constant';
import { UserIdentificationService } from '../../identification/services/user-identification.service';
import { UserOnboardingService } from '../../onboarding/services/user-onboarding.service';
import { OnlinePaymentService } from '../../payment/services/online-payment.service';
import { UserSimulationService } from '../../simulation/services/user-simulation.service';
import { UserSurveyService } from '../../survey/services/user-survey.service';
@Injectable({
  providedIn: 'root'
})
export class ContextService {
  apiUrl: string;
  constructor(
    private readonly factoryhelperService: FactoryhelperService,
    private readonly agTranslateService: AgTranslateService,
    private router: Router,
    private sessionService: SessionService,
    private customerService: CustomerService,
    private helperService: HelperService,
    private survey: UserSurveyService,
    private userSimulationService: UserSimulationService,
    private readonly backendEventLogging: BackendEventLogService,
    private userIdentificationService: UserIdentificationService,
    private readonly onboardingService: UserOnboardingService,
    private readonly authenticationService: AgAuthenticationService,
    private onlinePaymentService: OnlinePaymentService
  ) { 
    this.apiUrl = AgAppConfigService.get<string>('applicativeApiUrl');
  }

  decodeToken(token: string): any {
    try {
      // Split the token into parts
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      // Decode the payload (middle part of the token)
      const payload = JSON.parse(atob(parts[1]));

      return payload;
    } catch (error) {
      console.error('Error decoding token:', error.message);
      return null;
    }
  }

  getApplUser(token: string): string | null {
    const decodedPayload = this.decodeToken(token);
    return decodedPayload?.appl_user; // Return appl_user or null
  }

  logAccessTokenErrorInDB(message: string) {
    const isOneMSALogEnabled = AgAppConfigService.get<boolean>('OneMSAErrorLoggingEnabled');
    if(isOneMSALogEnabled) {
      const personid = isNaN(Number(localStorage.personId)) ? 116 : Number(localStorage.personId);
      const logReqModel: LogReqModel = {
        personId: personid, 
        onlinePaymentId: '',
        logLevel: LogLevel.DEBUG,
        message
      }
      this.onlinePaymentService.generateLog(logReqModel).subscribe();
    }
  }

  putUserPreferences (PersonID: string, optOutType: SilUnsubscribeType): Observable<UserPreferences> {
    const culture = this.agTranslateService.currentLang;
    const prospectModel = new prospect();
    prospectModel.Personguid = PersonID;
    prospectModel.SilUnsubscribeType = optOutType;
    const url = `${this.apiUrl}/${culture}/useraccount/userpreferences`;

    return this.factoryhelperService.put<{ Result: UserPreferences}>(url,prospectModel).pipe(map(x => x.Result));
  }

  postAuthActions(uci: string): void {
      const processCd = localStorage.getItem('ProcessCd');
      const currentHref = window.location.href;
      // Use RouteNames constants for esign and fromconnective
      const isEsign = currentHref.includes(`${RouteNames.eSign}`);
      const isFromConnective = currentHref.includes(`${RouteConstant.fromConnective}`);
      if (processCd === ProcessCd.SubscriptionFlow && !isEsign && !isFromConnective) {
        this.IsSubscriptionAllowed(uci);
      } 
      else if (processCd === ProcessCd.DirectLogin) {
        this.loginUser(uci);        
      }    
  }
  loginUser(uci: string) {
    const data : UserLoginRequestModel = {
      ProcessCd : ProcessCd.DirectLogin,
      Uci : uci
    }
    const hasData = uci && this.authenticationService.isAuthenticated;
    if(hasData) {
      const message = {
        Uci: uci,
        AccessToken: this.authenticationService.accessToken
      }         
      this.logAccessTokenErrorInDB(`ONE MSA loginuser API: ${JSON.stringify(message)}`);
      
      this.customerService.userLogin(data).subscribe({
        next: (res : UserLoginResponseModel) => { 
            localStorage.personId = res.PersonId;      
            let PersonId = res.PersonId, OnBoardingStatus = res.OnboardingStatus;
            if (!PersonId)
              void this.router.navigate([`${RouteNames.blocked}/${RouteNames.RedirectionScreenGeneric}`]);
            else if (PersonId && !([OnboardingStatus.ChildMigrated, OnboardingStatus.OnboardedInLEAD].includes(OnBoardingStatus)))
              void this.router.navigate([`${RouteNames.blocked}/${RouteNames.RedirectionScreenGeneric}`]);
            else if (PersonId && (OnBoardingStatus === OnboardingStatus.ChildMigrated))
              void this.router.navigate([`${RouteNames.blocked}/${RouteNames.RedirectionScreenChild}`]);
            else if (PersonId && (OnBoardingStatus === OnboardingStatus.OnboardedInLEAD)) {
              this.backendEventLogging.logBackendEvents(BeEvents.Login);
                this.retrievePersonDetails();
            }
        },
        error: (err) => {
          if (err?.status === 404) {
            void this.router.navigate([`${RouteNames.blocked}/${RouteNames.RedirectionScreenGeneric}`]);
          } else {
            this.helperService.handleAPIFailure(APIFailure.UserLogin, err?.status);
          }
        }
      });
    } else {
      void this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.logout]);     
    }
    
  }

  IsSubscriptionAllowed(uci: string): void {
    const userParams = this.sessionService.getUserParams();
    const productRangeCd = userParams?.partnercodeRange 
      ? userParams.partnercodeRange 
      : ProductCodeRange.Yongo;

    const data : IsSubscriptionAllowedReqObj = {
      Uci : uci, 
      ProductRangeCd : Number(productRangeCd)
    }
    if(uci && productRangeCd && this.authenticationService.isAuthenticated) {
      const message = {
        Uci: uci,
        AccessToken: this.authenticationService.accessToken
      }         
      this.logAccessTokenErrorInDB(`ONE MSA isSubscriptionAllowed API: ${JSON.stringify(message)}`);
      this.customerService.checkIsSubscriptionAllowed(data).subscribe({
        next: (res: IsSubscriptionAllowedResponse) => {
          const reason = res.IneligibilityReason;
          this.customerService.setSubscriptionAllowedReason = res as IsSubscriptionAllowedResponse;
          if(reason === null){
            this.createUser(uci);
          } 
          else {
            if (reason === EligibilityReason.InvalidAge) {
              this.backendEventLogging.logBackendEvents(BeEvents.InvalidAge);
              if (productRangeCd === ProductCodeRange.Uppie) {
                void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedAge}`]);
              } else if (productRangeCd === ProductCodeRange.Yongo) {
                void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedAgeRangeYongo}`]);
              }
            } 
            else if (reason == EligibilityReason.NotBelgianResident) {
              this.backendEventLogging.logBackendEvents(BeEvents.NotBelgianResident);
              if (productRangeCd === ProductCodeRange.Uppie) {
                void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedCountry}`]);
              }  else if (productRangeCd === ProductCodeRange.Yongo) {
                void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedCountryRangeYongo}`]);
              }
            } 
            else if ((reason === EligibilityReason.NissExpired) || (reason === EligibilityReason.NissExpiryMissing)) {
              if(reason === EligibilityReason.NissExpired){
                this.backendEventLogging.logBackendEvents(BeEvents.NissExpired);
              } 
              else if (reason === EligibilityReason.NissExpiryMissing){
                this.backendEventLogging.logBackendEvents(BeEvents.NissExpiryMissing);
              }
              void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockedExpiredIdentification}`]);
            }
          }
        },
        error: (err) => {
          this.helperService.handleAPIFailure(APIFailure.IsSubscriptionAllowedAPI, err?.status);
        }
      });
  } else {
    void this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.logout]);    
  }
  }

  createUser(uci: string): void {
    const reqObj = {
      Uci: uci,
      ProcessCd: ProcessCd.SubscriptionFlow,
      PartnerId: Number(this.sessionService.getUserParams().partnercode)
    }
    if(uci && this.authenticationService.isAuthenticated) {
      const message = {
        Uci: uci,
        AccessToken: this.authenticationService.accessToken
      }         
      this.logAccessTokenErrorInDB(`ONE MSA createuser API: ${JSON.stringify(message)}`);
      this.userIdentificationService.createUser(reqObj).subscribe({
        next: (res: createUserResponse) => {
            localStorage.personId = res.PersonId;
            this.retrievePersonDetails();           
        },
        error: (error) => {
          if(error?.status === 409) {
            void this.router.navigate([`${RouteNames.blocked}/${RouteNames.BlockUserNissUCIExists}`]);
          } else {
            this.helperService.handleAPIFailure(APIFailure.CreateUserAPI, error?.status);
          }
        }
      });
    }
    else {
      void this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.logout]);      
    }    
  }

  retrievePersonDetails(): void {
    if (this.authenticationService.isAuthenticated) {
      const partnerCode = Number(this.sessionService.getUserParams()?.partnercode);
      const partnerToken = this.sessionService.getUserParams()?.clientExtRef;
      const message = {
        Uci: localStorage.getItem('uci'),
        AccessToken: this.authenticationService.accessToken
      }         
      this.logAccessTokenErrorInDB(`ONE MSA retrievepersondetails API: ${JSON.stringify(message)}`);
      this.customerService.getCustomerDetailsFromDB(Number(localStorage.personId), partnerCode, partnerToken).subscribe({
        next: (personDetails: PersonDetails) => {
          this.helperService.createSession(SessionKeys.firstName, personDetails.PersonFirstName); 
          this.saveLanguage(personDetails.UserLanguage);
          this.customerService.setCustomerDetails(personDetails);
          sessionStorage.removeItem('userSurvey'); // removing the usersurvey session value (if any) just after login/registration
          if (this.sessionService.getDeeplinkParams()) {
              const deepLinkParams = this.sessionService.getDeeplinkParams();
              if (deepLinkParams.route && deepLinkParams.isDeepLinking === 'true') {
                this.navigateToDeepLink(deepLinkParams);
              }
          } 
          else {
            const processCd = localStorage.getItem('ProcessCd');
            const currentUrl = this.router.url;
              // As per the requirement if user is logging in with partner code from MOD1, then a quote will created on successful login
              // BUT NOT if we're currently on an e-sign confirmation page
              if (this.sessionService.getUserParams().partnercode && 
                  processCd !== ProcessCd.DirectLogin && 
                  !currentUrl.includes(RouteNames.eSign)) {
                if (this.sessionService.getUserParams().partnercodeRange === ProductCodeRange.Uppie || this.sessionService.getUserParams().partnercodeRange === ProductCodeRange.Yongo){
                  this.isContactAndTOUAvailable();                
                }
              } else {
                // Navigate to customer zone home for cases where survey flow is not triggered
                void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
              }
          }
        }, 
        error: (err) => {
          this.helperService.handleAPIFailure(APIFailure.RetrievePersonDetailsAPI, err?.status);
        }
      });
    } else {
      void this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.logout]);   
    }
    
  }
  
  saveLanguage(dbLang: string): void {
    const msaLang = this.authenticationService.user_language;
    if(dbLang !== msaLang){
      const reqObj = {
        PersonId: Number(localStorage.personId),
        UserLanguage: msaLang
      }
      this.customerService.saveUserLanguage(reqObj).subscribe();
    }
  }

  isContactAndTOUAvailable(isPostCall?: boolean) {
    forkJoin({
      contactDetails: this.onboardingService.getContactDetails(Number(localStorage.personId)).pipe(
        catchError((_error) => {
          return of(null);
        })
        ),
        termsOfUse: this.customerService.getTermsOfUseConsent(Number(localStorage.personId)).pipe(
          catchError((_error) => {
            return of(null);
          })
        ),
    }).subscribe(({contactDetails, termsOfUse}) => {
      if(contactDetails && termsOfUse) {
        this.handleRedirection(contactDetails,termsOfUse, isPostCall);
      } else {
        if(isPostCall) {
          this.helperService.showGenericErrorToast();
        } else {
          this.helperService.handleAPIFailure('isContactAndTOUAvailable');
        }
      }
    });
  }

  handleRedirection(contactDetails: ContactDetails, termsOfUse: TOUResponseModel, isPostCall: boolean): void {
    if(contactDetails.IsLeadContactDataAvailable && termsOfUse.IsTermsOfUseConsentAvailable) {
      if(!this.customerService.getCustomerDetails()) {          
        isPostCall ? this.getCustomerDetailsToNavigate(false) : this.getCustomerDetailsToNavigate(true);        
      } else {
        isPostCall ? this.continueNavigate(false) : this.continueNavigate(true);
        
      }
    }
    else {
      void this.router.navigate(['/' + RouteNames.authentication + '/' + RouteNames.ContactAndConsent]);
    }
  }

  getCustomerDetailsToNavigate(isFatcaScreenBackButtonHidden?: boolean): void {
    const message = {
      Uci: localStorage.getItem('uci'),
      AccessToken: this.authenticationService.accessToken
    }         
    this.logAccessTokenErrorInDB(`ONE MSA retrievepersondetails API: ${JSON.stringify(message)}`);
    this.customerService.getCustomerDetailsFromDB(Number(localStorage.personId)).pipe(
      tap((personDetails: PersonDetails) => {
        this.customerService.setCustomerDetails(personDetails);
      })
    ).subscribe({
      next: (res) => {
        this.continueNavigate(isFatcaScreenBackButtonHidden);
      },
      error: () => {
        this.helperService.showGenericErrorToast();
      }
    });
  }

  continueNavigate(isFatcaScreenBackButtonHidden?: boolean): void {
    if ((this.customerService.getCustomerDetails().SimulationDetailsList?.filter(x=> x.SimulationStatus === StatusCode.Active && 
      (x.ProductCode === ProductCodeRange.Uppie || x.ProductCode === ProductCodeRange.Yongo))?.length < this.customerService.maxSimulationsForClient) || (this.customerService.getCustomerDetails().SimulationDetailsList === null)) {
       this.customerService.startSimulation(SurveyFlow.CreateQuote, isFatcaScreenBackButtonHidden); 
    } else {
      this.customerService.isSimulationQuotaOver = true;
      void this.router.navigate(['/' + RouteNames.customerZone + '/' + RouteNames.customerHome]);
    }
  }

  navigateToDeepLink(deepLinkParams : DeepLinkingModel): void {
    if (deepLinkParams.route === 'AdviceIntro') {
      const selectedPartnerCode = this.sessionService.getUserParams()?.partnercode;
      this.userSimulationService.getStartParametersForValidation(selectedPartnerCode).pipe(
        map((x: {Result: StartParameter}) => x.Result),
        switchMap((d: StartParameter) => {
          this.sessionService.setHeaderContext(null, null, null, d.PartnerLogo, d.PartnerLinkFR, d.PartnerLinkNL);
          return this.survey.restoreSurveySessionValues(deepLinkParams.simulationId);
        }))
        .subscribe((res: SimulationSessionData) => {
          if (res.IsSimulationActive) {
            void this.router.navigate([`/${this.survey.AdviceIntroSectionId}/${RouteNames.surveyModule}/${RouteNames.questions}/${this.survey.AdviceIntroQuestionId}/${RouteNames.AdviceIntro}`]);
          }
          else {
            void this.router.navigate([`/${RouteNames.blockAdvice}`]);
          }
        });
    }
    if (deepLinkParams.route === 'NewSepa'){
      this.customerService.getContractReferenceForNewSepa(deepLinkParams.planId, Number(localStorage.personId))
        .subscribe((res: SepaDeeplinkModel) => {
          if(res.IsPersonLinkedToPlanId && res.ContractReferenceNumber){
            this.customerService.setCurrentSepaDetails = {
              PersonID: Number(localStorage.personId),
              ContractExtRef: res.ContractReferenceNumber,
              PlanID: deepLinkParams.planId,
              Amount: 0,
              IBAN: ''
            }
            void this.router.navigate([`/${RouteNames.customerZone}/${RouteNames.createsepamandate}`]);
          }
          else {
            void this.router.navigate([`/${RouteNames.blocked}`]);
          }
        })        
    }
    if (deepLinkParams.route === 'OnlinePaymentPopUp'){
      this.OnlinePaymentDeeplink();
    }
    if (deepLinkParams.route === 'Unsubscribe') {
      void this.router.navigate([`/${RouteNames.customerZone}/${RouteNames.myprofile}`]);
    }
  }

  OnlinePaymentDeeplink(): void {
    const deepLinkParams = this.sessionService.getDeeplinkParams();
    this.customerService.getContractReferenceForNewSepa(deepLinkParams.planId, Number(localStorage.personId)).subscribe({
      next : (res: SepaDeeplinkModel) => {
        if(res.IsPersonLinkedToPlanId && res.ContractReferenceNumber){
          this.sessionService.setOnlinePaymentInfo({entryPoint:OnlinePaymentEntryPage.CustomerZone})
          void this.router.navigate([`${RouteNames.OnlinePayment}`],{
                state : {
                  paymentDetails:{
                    planId:  deepLinkParams.planId,
                    ContractExternalReference: res.ContractReferenceNumber,
                    amount: deepLinkParams.amount
                  } as PaymentDetails
                }
              });
        }
        else {
          void this.router.navigate([`/${RouteNames.blocked}`]);
        }
      }, 
      error: (err) => {
        this.helperService.handleAPIFailure(APIFailure.ValidatePerson, err?.status);
      }
    });
  }
} 
